import React, { useEffect, useState } from 'react';

import Strings from '../../../Strings';
import SimpleModal from '../../../containers/Modal/SimpleModal';
import { validatePassword } from '../../../utils/validation';
import { MultiSelectField, PasswordInputField, SelectField } from '../../../containers/Form';
import PasswordRequirementsDisplay from '../../../components/PasswordRequirements';
import { allowedExtraRoles, PENDING_ROLE_ARCHETYPE } from './UserDetail';
import { isSuperUser } from '../../../utils/userRoles';
import { groupOrganizations } from 'components/OrgSelect/OrgSelect';
import { connect } from 'react-redux';
import { actions } from 'components/OrgSelect/orgFilterActions';
import { SelectedOptions, SelectedOptionsType } from 'components/Select/Multiselect';

export function PasswordResetModal({ user, passwordResetLoading, onClose, onSubmit, loading }) {
  const [method, setMethod] = useState(null);

  return (
    <SimpleModal
      name="password-reset-modal"
      title={Strings.sendPasswordReset || 'Send Password Reset'}
      onCancel={onClose}
      onSubmit={() => onSubmit(method)}
      isSubmitDisabled={!method || loading}
      content={(
        <>
          <div>{Strings.choosePasswordResetMethod || 'Choose how to send the password reset link:'}</div>
          <div className="password-reset-modal__methods">
            <div
              className={`password-reset-modal__method-option ${user.email ? '' : 'password-reset-modal__method-option--disabled'}`}
              onClick={() => user.email && !loading && setMethod('email')}
            >
              <div className="password-reset-modal__method-label">{Strings.email || 'Email'}</div>
              <div className="password-reset-modal__method-value">{user.email || 'Not available'}</div>
            </div>
            <div
              className={`password-reset-modal__method-option ${user.phone ? '' : 'password-reset-modal__method-option--disabled'}`}
              onClick={() => user.phone && !loading && setMethod('sms')}
            >
              <div className="password-reset-modal__method-label">{Strings.sms || 'SMS'}</div>
              <div className="password-reset-modal__method-value">{user.phone || 'Not available'}</div>
            </div>
          </div>
        </>
      )}
    />
  );
}

export function SetPasswordModal({ user, onClose, onSubmit, loading }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [touched, setTouched] = useState({});


  const markTouched = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const shouldHighlight = (field) => {
    return touched[field];
  };

  useEffect(() => {
    const errors = [];

    if (newPassword === '' || !validatePassword(newPassword).isValid) {
      errors.push({ property: 'newPassword', errors: [Strings.errors.invalidPassword] });
    }

    if (newPassword != confirmPassword) {
      errors.push({ property: 'confirmPassword', errors: [Strings.errors.passwordsDontMatch] });
    }

    setValidationErrors(errors);
  }, [newPassword, confirmPassword]);

  return (
    <SimpleModal
      name="set-password-modal"
      title={Strings.setPassword || 'Set Password'}
      onCancel={onClose}
      onSubmit={() => onSubmit(newPassword)}
      isSubmitDisabled={validationErrors.length > 0 || loading}
      content={(
        <>
          <div>{Strings.setPasswordDescription || `Set a new password for ${user.first_name} ${user.last_name}.`}</div>
          <PasswordInputField
            name="newPassword"
            id="newPassword"
            label={Strings.newPassword}
            value={newPassword || ''}
            onChange={e => setNewPassword(e.target.value)}
            onBlur={() => markTouched('newPassword')}
            isRequired
            highlightInvalid={shouldHighlight('newPassword')}
            errorsForTooltip={shouldHighlight('newPassword') ? validationErrors.find(v => v.property === 'newPassword')?.errors || [] : []}
          />
          <PasswordRequirementsDisplay
            requirements={validatePassword(newPassword).requirements}
            strength={validatePassword(newPassword).strength}
            strengthLabel={validatePassword(newPassword).strengthLabel}
            show={newPassword.length > 0 || touched.newPassword}
          />
          <PasswordInputField
            name="confirmPassword"
            id="confirmPassword"
            label={Strings.confirmPassword}
            value={confirmPassword || ''}
            onChange={e => setConfirmPassword(e.target.value)}
            onBlur={() => markTouched('confirmPassword')}
            isRequired
            highlightInvalid={shouldHighlight('confirmPassword')}
            errorsForTooltip={shouldHighlight('confirmPassword') ? validationErrors.find(v => v.property === 'confirmPassword')?.errors || [] : []}
          />
        </>
      )}
    />
  );
}

export function AddRoleModal({ organizations, onClose, onSubmit, loading, userRoles, isPatientUser, userOrgId }) {
  const [addRoleOrgId, setAddRoleOrgId] = useState('');
  const [addRoleRoleId, setAddRoleRoleId] = useState('');
  const [addRoleFilteredRoles, setAddRoleFilteredRoles] = useState([]);

  const normalizeOrgId = (orgId) => {
    if (!orgId || orgId === '00000000-0000-0000-0000-000000000000') {
      return 'orgless';
    }

    return String(orgId);
  };

  const isCrossOrgPatientSelection = isPatientUser
    && addRoleOrgId
    && normalizeOrgId(addRoleOrgId) !== normalizeOrgId(userOrgId);

  // Filter roles for add role modal based on selected org
  React.useEffect(() => {
    if (organizations.length > 0 && addRoleOrgId) {
      const isOrgless = addRoleOrgId === 'orgless';
      if (isOrgless) {
        const orglessOrg = organizations.find(org => org.id === 'orgless');
        setAddRoleFilteredRoles(orglessOrg?.roles.map(r => ({ ...r, value: r.role_id, label: r.title })) || []);
      } else {
        const selectedOrg = organizations.find(org => String(org.id) === String(addRoleOrgId));
        setAddRoleFilteredRoles(selectedOrg?.roles.map(r => ({ ...r, value: r.role_id, label: r.title }))  || []);
      }
    } else {
      setAddRoleFilteredRoles([]);
    }
  }, [addRoleOrgId, organizations]);

  return (
    <SimpleModal
      name="add-role-modal"
      title={Strings.addRole || "Add Role"}
      onCancel={onClose}
      onSubmit={() => onSubmit(addRoleRoleId, addRoleOrgId)}
      isSubmitDisabled={!addRoleOrgId || !addRoleRoleId || loading || isCrossOrgPatientSelection}
      content={(
        <>
          <SelectField
            name="organizationRole"
            label={Strings.organization || 'Organization'}
            value={addRoleOrgId}
            onChange={option => {
              setAddRoleOrgId(option.value);
              setAddRoleRoleId('');
            }}
            data={organizations.filter(org => org.id !== 'orgless').concat(isSuperUser() ? [{ value: 'orgless', label: Strings.orgless || 'Orgless' }] : [])}
            isSearchable
          />
          <SelectField
            name="role"
            label={Strings.role || 'Role'}
            value={addRoleRoleId}
            onChange={option => setAddRoleRoleId(option.value)}
            data={
              addRoleFilteredRoles
                .filter(role => role.archetype !== PENDING_ROLE_ARCHETYPE && allowedExtraRoles.includes(role.archetype))
                .filter(role => !userRoles.some(ur => ur.uuid === role.id))
                .map(role => ({ value: role.id, label: role.title }))
            }
            isDisabled={!addRoleOrgId}
          />
          {isCrossOrgPatientSelection && (
            <div className="error-message">
              {Strings.roleSwitcher?.patientRoleCannotBeExtended}
            </div>
          )}
        </>
      )}
    />
  );
}

function AddBillingOrgModalComponent({ billingOrgs, onClose, onSubmit, loading, getOrgs }) {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState(billingOrgs || []);
  
  React.useEffect(() => {
    getOrgs().then(resp => {
      const orgData = resp.response?.data;
      if (Array.isArray(orgData)) {
        setOrganizations(orgData);
      } else {
        setOrganizations([]);
      }
    });
  }, [getOrgs]);

  return (
    <SimpleModal
      name="add-billing-org-modal"
      title={Strings.addBillingOrgs || 'Add Billing Organizations'}
      onCancel={onClose}
      onSubmit={() => onSubmit(selectedOrgIds)}
      isSubmitDisabled={loading}
      content={(
        <>
          <MultiSelectField
            name="billingOrganization"
            label={Strings.billingOrganization || 'Billing Organization'}
            value={selectedOrgIds}
            onChange={options => setSelectedOrgIds(options.map(option => option.value))}
            data={groupOrganizations(organizations)}
            isSearchable
            placeholder={Strings.selectBillingOrgs || 'Select billing organizations'}
            closeMenuOnSelect={false}
          />
          <SelectedOptions
            id="billing-orgs"
            items={
              selectedOrgIds.map(orgId => {
                const org = organizations.find(o => o.masked_id === orgId);
                return org ? { ...org, value: org.masked_id, label: org.name } : null;
              }).filter(Boolean)
            }
            label={Strings.associatedBillingOrgs || 'Associated Billing Orgs'}
            options={organizations.map(org => ({ ...org, value: org.masked_id, label: org.name }))}
            className="grid-full-width"
            withClearButton={false}
          />
        </>
      )}
    />
  );
}

const mapDispatchToProps = {
  getOrgs: actions.getMyOrgs,
};

export const AddBillingOrgModal = connect(null, mapDispatchToProps)(AddBillingOrgModalComponent);